import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisH, faCirclePlus, faCircleCheck, faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import EntityTable from '../../components/Listing/EntityTable';
import Button from '../../components/Form/Button';
import FabGroup from '../../components/FabGroup';
import BackofficeProductModal from '../../components/Modal/BackofficeProductModal';
import { usePageTitle } from '../../layouts/PageTitleContext';
import { API_BASE_URL } from '../../api';

export default function BackofficeProducts() {
    const { setTitle, setTitleIcon, setTitleDescription } = usePageTitle();
    const [editingProduct, setEditingProduct] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const tableRef = useRef(null);

    useEffect(() => {
        document.title = 'Backoffice — Produits';
        setTitle('Produits');
        setTitleDescription('Catalogue produits de toute la plateforme');
        setTitleIcon('ti-vaccine-bottle');
        return () => { setTitleDescription(null); setTitleIcon(null); };
    }, [setTitle, setTitleDescription, setTitleIcon]);

    const reload = () => tableRef.current?.reload();

    const handleDelete = (row) => {
        axios.delete(`${API_BASE_URL}/api/v1/backoffice/products/${row.id}`, { withCredentials: true })
            .then(() => {
                toast.success('Produit supprimé.');
                reload();
            })
            .catch((error) => {
                toast.error(error.response?.data?.message || 'Une erreur est survenue.');
            });
    };

    const columns = [
        {
            Header: 'Statut',
            accessor: 'status',
            sortable: true,
            className: 'text-center',
            Cell: (row) => (
                <FontAwesomeIcon
                    icon={row.status === 'MARKETED' ? faCircleCheck : faCircleXmark}
                    style={{ color: row.status === 'MARKETED' ? '#16a34a' : '#9ca3af', fontSize: 16 }}
                    data-tooltip-id="tooltip"
                    data-tooltip-content={row.status === 'MARKETED' ? 'Commercialisé' : 'Non commercialisé'}
                />
            ),
        },
        {
            Header: 'Produit',
            accessor: 'name',
            sortable: true,
            minWidth: '380px',
            Cell: (row) => (
                <div className="d-flex align-items-start gap-3" style={{ paddingBlock: 4 }}>
                    <img
                        src={row.images?.length > 0 ? row.images[0].url : '/images/no_product.png'}
                        alt={row.name}
                        style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
                    />
                    <div style={{ minWidth: 0, maxWidth: 340 }}>
                        <span className="cell-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                            {row.name}
                        </span>
                        {(row.presentation || row.dosage) && (
                            <span className="cell-sub" style={{ display: 'block', marginTop: 5, color: '#111827' }}>
                                {row.presentation}{row.presentation && row.dosage ? ' · ' : ''}{row.dosage}
                            </span>
                        )}
                        {row.laboratoryName && (
                            <span className="cell-sub" style={{ display: 'block', marginTop: 5, color: '#374151' }}>
                                Par {row.laboratoryName}
                            </span>
                        )}
                    </div>
                </div>
            ),
        },
        {
            Header: 'Pharmacie',
            accessor: 'pharmacyName',
            sortable: false,
            Cell: (row) => row.pharmacyId
                ? <span>{row.pharmacyName}</span>
                : <span className="badge bg-light text-dark border">Catalogue partagé</span>,
        },
        {
            Header: 'Prix',
            accessor: 'publicPrice',
            sortable: true,
            Cell: (row) => row.publicPrice != null ? `${Number(row.publicPrice).toFixed(2)} dhs` : '—',
        },
        {
            Header: 'Code-barres',
            accessor: 'barcode',
            sortable: true,
            Cell: (row) => row.barcode || '—',
        },
        {
            accessor: 'actions',
            Header: '',
            className: 'col-actions',
            Cell: (row) => (
                <div className="cell-dropdown" onClick={(e) => e.stopPropagation()}>
                    <button className="cell-actions-btn dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                        <FontAwesomeIcon icon={faEllipsisH} />
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end">
                        <li>
                            <button className="dropdown-item" onClick={() => setEditingProduct(row)}>Modifier</button>
                        </li>
                        <li>
                            <Button className="dropdown-item text-danger" onConfirm={() => handleDelete(row)}>
                                Supprimer
                            </Button>
                        </li>
                    </ul>
                </div>
            ),
        },
    ];

    // Card-view design for this listing (Table's enableCardView feature, forced on mobile)
    // — reuses the actions column's own dropdown so both views offer identical actions.
    const actionsColumn = columns.find((c) => c.accessor === 'actions');
    const renderProductCard = (row) => (
        <div className="card h-100 shadow-sm" style={{ cursor: 'pointer' }} onClick={() => setEditingProduct(row)}>
            <div className="card-body d-flex flex-column" style={{ padding: 14, gap: 6 }}>
                <div className="d-flex align-items-center justify-content-between">
                    <FontAwesomeIcon
                        icon={row.status === 'MARKETED' ? faCircleCheck : faCircleXmark}
                        style={{ color: row.status === 'MARKETED' ? '#16a34a' : '#9ca3af', fontSize: 15 }}
                    />
                </div>

                <div className="d-flex align-items-start gap-3">
                    <img
                        src={row.images?.length > 0 ? row.images[0].url : '/images/no_product.png'}
                        alt={row.name}
                        style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
                    />
                    <div style={{ minWidth: 0 }}>
                        <span style={{ fontSize: 13.5, fontWeight: 600, color: '#111827', display: 'block' }}>{row.name}</span>
                        {(row.presentation || row.dosage) && (
                            <span style={{ fontSize: 11.5, color: '#6b7280', display: 'block', marginTop: 3 }}>
                                {row.presentation}{row.presentation && row.dosage ? ' · ' : ''}{row.dosage}
                            </span>
                        )}
                        {row.laboratoryName && (
                            <span style={{ fontSize: 11.5, color: '#9ca3af', display: 'block', marginTop: 3 }}>
                                Par {row.laboratoryName}
                            </span>
                        )}
                    </div>
                </div>

                <span style={{ fontSize: 11.5, color: '#6b7280' }}>
                    {row.pharmacyId ? row.pharmacyName : 'Catalogue partagé'}
                </span>

                <div className="d-flex align-items-center justify-content-between" style={{ marginTop: 'auto', paddingTop: 6 }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>
                        {row.publicPrice != null ? `${Number(row.publicPrice).toFixed(2)} dhs` : '—'}
                    </span>
                    {actionsColumn?.Cell(row)}
                </div>
            </div>
        </div>
    );

    return (
        <>
            <EntityTable
                ref={tableRef}
                endpoint={`${API_BASE_URL}/api/v1/backoffice/products`}
                columns={columns}
                defaultSort="name"
                defaultDirection="asc"
                pageSize={20}
                emptyMessage="Aucun produit"
                searchable={true}
                searchPlaceholder="Rechercher un produit..."
                rowId="id"
                onRowClick={(row) => setEditingProduct(row)}
                enableCardView={true}
                renderCard={renderProductCard}
            />

            <FabGroup buttons={[
                { label: 'Nouveau produit', className: 'btn-primary', icon: faCirclePlus, onClick: () => setShowCreateModal(true) },
            ]} />

            {showCreateModal && (
                <BackofficeProductModal
                    show={showCreateModal}
                    onClose={() => setShowCreateModal(false)}
                    onSave={reload}
                />
            )}

            {editingProduct && (
                <BackofficeProductModal
                    product={editingProduct}
                    show={!!editingProduct}
                    onClose={() => setEditingProduct(null)}
                    onSave={reload}
                />
            )}
        </>
    );
}
